
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { findNew } from '@/helpers/cardInfoHelperFunctions/findNewHelper';
import GET_POSTINGS from '@/apollo/dashboard/GetPostings.gql';
import GET_MANAGER_DB from '@/apollo/dashboard/GetManagerDB.gql';

import DashboardCardScaffold from '@/components/dashboard/cards/CardScaffoldContractors.vue';
import CardItemScaffold from '@/components/dashboard/cards/CardItemScaffold.vue';
import DashboardContractorInvite from '@/components/companyContractors/DashboardContractorInvite.vue';

import ContractorsIcon from '@/components/icons/ContractorsIcon.vue';
import SimplePersonIcon from '@/components/icons/SimplePersonIcon.vue';
import ItemChipsWithMore from '@/components/common/ItemChipsWithMore.vue';
import PostingModalWithButton from '@/components/postings/PostingModalWithButton.vue';
import timeRangeForNew from '@/helpers/cardInfoHelperFunctions/timeRangeForNew';
import CircleWithInitialsData from '@/entities/CircleWithInitialsData';
import { Posting } from '@/generated/graphql';
import GET_FAVORITES from '@/apollo/dashboard/getFavorites.gql';

import { Contractor } from '@/generated/graphql';
import RouteNames from '@/entities/RouteNames';
import RouteParams from '@/entities/RouteParams';
import ItemChipsData from '@/entities/ItemChipsData';
import Roles from '@/entities/Roles';
import getSingularPluralForm from '@/helpers/cardInfoHelperFunctions/getSingularPluralForm';
import { key } from 'localforage';
import PlusIcon from '@/components/icons/PlusIcon.vue';
import CircleWithInitials from '@/components/card/CircleWithInitials.vue';

import { hslColorFromName } from '@/utils/hslColorFromName';
import S3Image from '@/components/common/s3-image.vue';
import Tooltip from '@/components/common/Tooltip.vue';
import TooltipVariants from '@/entities/TooltipVariants';

interface ContractorInfo {
	id: string;
	name: string;
	avatar?: string;
	routeParams: RouteParams<{ id: string }>;
}

@Component({
	components: {
		DashboardCardScaffold,
		DashboardContractorInvite,
		CardItemScaffold,
		ContractorsIcon,
		PostingModalWithButton,
		SimplePersonIcon,
		ItemChipsWithMore,
		CircleWithInitials,
		PlusIcon,
		S3Image,
		Tooltip,
	},
	apollo: {
		managerDB: {
			query: GET_MANAGER_DB,
			variables() {
				return this.managerFilter;
			},
			update: (data) => data,
		},
		dashboardPostings: {
			query: GET_POSTINGS,
			variables() {
				return {
					postingFilter: this.filter.postings,
					newPostingFilter: this.filter.newPostings,
					applicationFilter: this.filter.applications,
				};
			},
			update(data) {
				return data;
			},
		},
		favorites: {
			query: GET_FAVORITES,
			variables() {
				const managerId = this.currentUser.manager?.id;
				const managerConditions = !managerId
					? {}
					: {
							managers: {
								_and: [
									{
										manager: {
											id: {
												_eq: managerId,
											},
										},
									},
									{
										approved: {
											_eq: true,
										},
									},
								],
							},
					  };

				return {
					favoritesFilter: {
						_and: [
							{
								approved: {
									_eq: true,
								},
								deleted: {
									_eq: false,
								},
							},
							{
								user: {
									favorited_by_users: {
										favorited_by_id: {
											_eq: this.currentUserId,
										},
									},
								},
							},
							managerConditions,
						],
					},
				};
			},
			update(data) {
				return data;
			},
		},
	},
})
export default class DashboardContractors extends Vue {
	@Prop({ type: String, required: true }) readonly companyId!: string;
	@Prop({ type: String, required: true }) readonly currentUserId!: string;
	@Prop({ type: String, required: true }) readonly currentUserRole!: string;
	@Prop({ type: String, required: true })
	readonly companyBusinessName!: string;
	@Prop({ type: Array }) readonly contractors!: Contractor[];
	@Prop({ type: Array, default: () => [] })
	readonly contractorsAvatar!: Contractor[];
	@Prop({ type: Number, default: undefined })
	readonly contractorsTotal?: number;
	@Prop({ type: Number, default: undefined })
	readonly newContractorsTotal?: number;
	@Prop({ type: String, default: undefined })
	readonly currentUserManagerId?: string;
	@Prop({ type: Boolean, default: false }) readonly isAdmin!: boolean;
	@Prop({ type: Object, required: true }) readonly currentUser!: any;
	@Prop({ type: String, default: 'h-8 w-8' }) readonly avatarClasses!: string;
	@Prop({ type: Boolean, default: false }) readonly highlightAvatar!: boolean;
	@Prop({ type: Boolean, default: false }) readonly primary!: boolean;
	@Prop({ type: String, default: undefined }) readonly initial?: string;

	routes = RouteNames;
	contractorsTotalText = '';
	dashboardPostings: any = null;
	favorites: any = null;
	managerDB: any = null;
	TooltipVariants = TooltipVariants;

	get filter(): object {
		const postingConditions: any[] = [{ active: { _eq: true } }];
		if (this.currentUser.role == Roles.MANAGER) {
			postingConditions.push({
				manager: {
					user: {
						id: { _eq: this.currentUser.id },
					},
				},
			});
		}
		return {
			postings: { _and: postingConditions },
			newPostings: {
				_and: [
					{ created_at: { _gt: timeRangeForNew(this.$moment) } },
					{ active: { _eq: true } },
				],
			},
			applications: { posting: { active: { _eq: true } } },
		};
	}

	get postings(): CircleWithInitialsData[] {
		if (!this.dashboardPostings?.postings) {
			return [];
		}
		return this.dashboardPostings.postings.map((p: Posting) => {
			return {
				name: p.job_title ?? '',
				id: p.id,
			};
		});
	}
	get total() {
		if (this.favorites) {
			return this.favorites.total.aggregate.count;
		}
	}
	get postingsTotal(): undefined | number {
		if (!this.dashboardPostings?.postingsTotal) {
			return undefined;
		}
		return this.dashboardPostings.postingsTotal.aggregate.count;
	}

	get newPostings(): CircleWithInitialsData[] {
		if (!this.dashboardPostings?.postings) {
			return [];
		}
		const newItems = this.dashboardPostings.postings.filter((p: Posting) =>
			findNew(p.created_at, this.$moment)
		);

		if (!newItems.length) {
			return [];
		}

		return newItems.map((p: Posting) => {
			return {
				name: p.job_title ?? '',
				id: p.id,
			};
		});
	}

	get newPostingsTotal(): undefined | number {
		if (!this.dashboardPostings?.newPostingsTotal) {
			return undefined;
		}
		return this.dashboardPostings.newPostingsTotal.aggregate.count;
	}

	get defaultInitialsClasses(): string {
		return 'inline-block object-cover object-center inline-flex h-8 w-8 rounded-full items-center justify-center';
	}

	get defaultImageClasses(): string {
		return 'inline-block rounded-full object-cover object-center';
	}

	get avatarStyleClasses(): Record<string, boolean> {
		return {
			[this.avatarClasses]: Boolean(this.avatarClasses),
			['border border-primary']: this.highlightAvatar,
		};
	}

	get bgStyle(): object {
		return {
			background: '#1A7BEE',
		};
	}

	get applicantsTotal(): undefined | number {
		if (!this.dashboardPostings?.applicants) {
			return undefined;
		}
		// return this.dashboardPostings.applicants
		//   .map((a: any) => {
		//     return a.applications_aggregate.aggregate.count;
		//   })
		//   .reduce((a: number, b: number) => a + b, 0);
		return this.dashboardPostings?.applicants.aggregate.count;
	}

	isAccessAllowed(contractor: Contractor): boolean {
		let hasAccess: boolean = false;
		if (this.currentUserManagerId && contractor.managers) {
			hasAccess = contractor.managers.some((m) => {
				return m.manager_id === this.currentUserManagerId && m.approved;
			});
		}
		return hasAccess || this.isAdmin;
	}

	get isManager(): boolean {
		return this.currentUserRole === Roles.MANAGER;
	}

	get newContractorsText(): string {
		return `New ${getSingularPluralForm(
			this.newContractorsTotal,
			'Contractor',
			'Contractors'
		)}`;
	}

	get contractorsInfo(): ContractorInfo[] {
		if (this.contractors == undefined || this.contractors.length == 0) {
			return [];
		}

		return this.contractors.map((contractor) => {
			const baseInfo = {
				name: `${contractor.user.given_name} ${contractor.user.family_name}`,
				avatar: contractor.user.avatar || undefined,
			};
			if (this.isAccessAllowed(contractor)) {
				return {
					...baseInfo,
					id: contractor.id,
					routeParams: {
						name: RouteNames.CONTRACTOR_DETAILS_ABOUT,
						params: { id: contractor.id },
					},
				};
			} else {
				return {
					...baseInfo,
					id: contractor.user.id,
					routeParams: {
						name: RouteNames.CONTRACTOR_PUBLIC_PAGE,
						params: { id: contractor.user.id },
					},
				};
			}
		});
	}

	get newContractors(): { contractors: ItemChipsData[]; total: number } {
		if (this.contractors == undefined || this.contractors.length == 0) {
			const newContractors = this.contractors.filter(
				(contractor: Contractor) =>
					findNew(contractor.created_at, this.$moment)
			);
			if (!newContractors.length) {
				return {
					contractors: [],
					total: 0,
				};
			}

			const contractorsInfo = newContractors.map(
				(contractor: Contractor) => {
					return {
						id: contractor.id,
						name: `${contractor.user.given_name} ${contractor.user.family_name}`,
						avatar: contractor.user.avatar || undefined,
					};
				}
			);

			return {
				contractors: contractorsInfo,
				total: newContractors.length,
			};
		} else {
			return {
				contractors: [],
				total: 0,
			};
		}
	}

	get managerFilter(): object {
		const contractorCommonConditions = [
			{ approved: { _eq: true } },
			{
				contractor: {
					_and: [
						{ deleted: { _eq: false } },
						{ approved: { _eq: true } },
					],
				},
			},
		];

		return {
			newContractorsFilter: {
				_and: [
					{ updated_at: { _gt: timeRangeForNew(this.$moment) } },
					...contractorCommonConditions,
				],
			},
			contractorsFilter: {
				_and: [...contractorCommonConditions],
			},
		};
	}

	get myNewContractorsText(): string {
		return `New ${getSingularPluralForm(
			this.newContractorsTotal,
			'Contractor',
			'Contractors'
		)}`;
	}

	get myContractorsInfo(): ItemChipsData[] | string {
		if (!this.managerDB) {
			return [];
		}
		return this.managerDB.contractors.map(({ contractor }: any) => {
			return {
				id: contractor.id,
				name: `${contractor.user.given_name} ${contractor.user.family_name}`,
				avatar: contractor.user.avatar,
			};
		});
	}

	get myNewContractorsInfo(): ItemChipsData[] {
		if (!this.managerDB) {
			return [];
		}

		const newContractors = this.managerDB.newContractors;
		if (!newContractors.length) {
			return [];
		}

		return newContractors.map(({ contractor }: any) => {
			return {
				id: contractor.id,
				name: `${contractor.user.given_name} ${contractor.user.family_name}`,
				avatar: contractor.user.avatar,
			};
		});
	}

	get myContractorsTotal(): number | undefined {
		if (!this.managerDB) {
			return undefined;
		}
		return this.managerDB.contractorsTotal.aggregate.count;
	}

	get myNewContractorsTotal(): number | undefined {
		if (!this.managerDB) {
			return undefined;
		}
		return this.managerDB.newContractorsTotal.aggregate.count;
	}

	get myContractors(): Contractor[] {
		if (!this.contractors.length) {
			return [];
		}
		const myContractorsList = this.contractors.filter((contractor) =>
			contractor.managers?.forEach(
				(manager) => manager.manager_id === this.currentUserId
			)
		);
		return myContractorsList;
	}

	getContractorFullname(contractor: Contractor): string {
		if (!contractor || !contractor?.user) {
			return '';
		}
		if (!contractor?.user?.given_name && contractor?.user?.family_name) {
			return contractor?.user?.family_name;
		}
		if (contractor?.user?.given_name && !contractor?.user?.family_name) {
			return contractor?.user?.given_name;
		}
		if (!contractor?.user?.given_name && !contractor?.user?.family_name) {
			return '';
		}
		return (
			contractor?.user?.given_name + ' ' + contractor?.user?.family_name
		);
	}
}
